import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, useMap, WMSTileLayer, ImageOverlay } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Layer, LayerType, LayerGroup, LayerGroupManager } from './types/layer';
import QGISLayerPanel from './components/QGISLayerPanel';
import LegendPanel from './components/LegendPanel';
import LegendEditor from './components/LegendEditor';
import HelpModal from './components/HelpModal';
import LanguageSelector from './components/LanguageSelector';
import { debounce } from './utils/debounce';
import { fetchLegendsBatch } from './utils/api';
import './App.css';
import './i18n'; // i18n 초기화

// 배경지도 타입 정의
type BackgroundMapType = 'osm' | 'cartodb' | 'satellite' | 'topo';

// 지도 리사이즈 및 이벤트 리스너 컴포넌트 (최적화)
const MapResizer: React.FC<{ onMapMove: () => void }> = React.memo(({ onMapMove }) => {
  const map = useMap();
  
  useEffect(() => {
    const handleResize = () => {
      // 지도 리사이즈를 더 안정적으로 처리
      setTimeout(() => {
        try {
          if (map && map.invalidateSize) {
            map.invalidateSize();
          }
        } catch (error) {
          console.warn('Map resize error:', error);
        }
      }, 100);
    };
    
    // 디바운스된 이벤트 핸들러
    let debounceTimer: NodeJS.Timeout;
    const debouncedHandler = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(onMapMove, 200);
    };
    
    // 지도 이동 이벤트 리스너 추가
    map.on('moveend', debouncedHandler);
    map.on('zoomend', debouncedHandler);
    
    // 초기 리사이즈 실행
    handleResize();
    
    return () => {
      clearTimeout(debounceTimer);
      map.off('moveend', debouncedHandler);
      map.off('zoomend', debouncedHandler);
    };
  }, [map, onMapMove]);
  
  return null;
});

// 모바일에서 지도 클릭 시 사이드바를 접는 컴포넌트
const MapClickHandler: React.FC<{ onMapClick: () => void }> = React.memo(({ onMapClick }) => {
  const map = useMap();
  
  useEffect(() => {
    const handleClick = () => {
      onMapClick();
    };
    
    map.on('click', handleClick);
    
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onMapClick]);
  
  return null;
});

// 배경지도 설정 (다국어 지원)
const getBackgroundMaps = (t: any) => ({
  osm: {
    name: t('map.openStreetMap'),
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    minZoom: 0,
    maxZoom: 19,
    maxNativeZoom: 19,
    tileSize: 256
  },
  cartodb: {
    name: 'CartoDB Positron',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '© CartoDB',
    minZoom: 0,
    maxZoom: 20,
    maxNativeZoom: 20,
    tileSize: 256
  },
  satellite: {
    name: t('map.satellite'),
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '© Esri',
    minZoom: 0,
    maxZoom: 19,
    maxNativeZoom: 19,
    tileSize: 256
  },
  topo: {
    name: t('map.topographic'),
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '© OpenTopoMap',
    minZoom: 0,
    maxZoom: 17,
    maxNativeZoom: 17,
    tileSize: 256
  }
});

// EGIS 토지피복 레이어 - 안정적인 구현
const egisLayer: Layer = {
  id: 'egis-landcover',
  name: 'EGIS 토지피복지도 2025', // 이 부분은 동적으로 번역됨
  type: LayerType.EGIS,
  visible: false, // 초기 접속 시 선택되지 않은 상태
  opacity: 1.0,
  groupId: 'egis-group',
  url: 'https://api.mcee.go.kr/geoserver/gwc/service/wms',
  params: {
    layers: 'EGIS:lv3_2025y',
    format: 'image/png',
    transparent: true,
    version: '1.1.1',
    service: 'WMS',
    request: 'GetMap'
  }
};

// EGIS 레이어: 코드의 최신 url/params 유지, 사용자 visible/opacity만 복원
const mergeEgisLayerSettings = (savedLayer: Layer | undefined, egisName: string): Layer => ({
  ...egisLayer,
  name: egisName,
  visible: savedLayer?.visible ?? egisLayer.visible,
  opacity: savedLayer?.opacity ?? egisLayer.opacity,
  groupId: 'egis-group',
});

// localStorage 복원 시 EGIS 그룹은 최신 접속정보로 덮어쓰기
const mergeSavedLayerGroups = (savedGroups: LayerGroup[], egisName: string): LayerGroup[] => {
  const defaultGroups = initializeLayerGroups((key: string) => key);

  return defaultGroups.map(defaultGroup => {
    const savedGroup = savedGroups.find(group => group.id === defaultGroup.id);
    if (!savedGroup) {
      return defaultGroup.id === 'egis-group'
        ? { ...defaultGroup, layers: [mergeEgisLayerSettings(undefined, egisName)] }
        : defaultGroup;
    }

    if (defaultGroup.id === 'egis-group') {
      const savedEgis = savedGroup.layers.find(
        layer => layer.type === LayerType.EGIS || layer.id === egisLayer.id
      );
      return {
        ...savedGroup,
        layers: [mergeEgisLayerSettings(savedEgis, egisName)],
      };
    }

    return savedGroup;
  });
};

// 그룹 레이어 초기화 함수
const initializeLayerGroups = (t: any): LayerGroup[] => {
  return [
    {
      id: 'background-group',
      name: t('common.backgroundMap'),
      type: 'background',
      layers: [], // 배경지도는 별도로 관리
      expanded: false, // 초기 접속 시 접힌 상태
      order: 0,
      visible: true,
      opacity: 1.0
    },
    {
      id: 'egis-group',
      name: t('common.egisLandCover'),
      type: 'egis',
      layers: [egisLayer],
      expanded: false, // 초기 접속 시 접힌 상태
      order: 1,
      visible: true,
      opacity: 1.0
    },
    {
      id: 'user-group',
      name: t('common.userUploadData'),
      type: 'user',
      layers: [],
      expanded: false, // 초기 접속 시 접힌 상태
      order: 2,
      visible: true,
      opacity: 1.0
    }
  ];
};

function App() {
  const { t, i18n } = useTranslation();
  
  // 지원되는 언어 목록
  const supportedLanguages = ['ko', 'en', 'fr'];
  
  // 언어 초기화
  useEffect(() => {
    const initializeLanguage = () => {
      // localStorage에서 저장된 언어 설정 확인
      const savedLanguage = localStorage.getItem('i18nextLng');
      if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
        i18n.changeLanguage(savedLanguage);
        return;
      }

      // 브라우저의 기본 언어 감지
      const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
      const primaryLanguage = browserLanguage.split('-')[0]; // 'ko-KR' -> 'ko'

      // 지원되는 언어인지 확인
      if (supportedLanguages.includes(primaryLanguage)) {
        i18n.changeLanguage(primaryLanguage);
        localStorage.setItem('i18nextLng', primaryLanguage);
      } else {
        // 지원되지 않는 언어인 경우 영어를 기본값으로 설정
        i18n.changeLanguage('en');
        localStorage.setItem('i18nextLng', 'en');
      }
    };

    initializeLanguage();
  }, [i18n]);
  
  // 배경지도 설정 (다국어 지원)
  const backgroundMaps = getBackgroundMaps(t);
  
  // EGIS 레이어를 동적으로 생성 (다국어 지원)
  const egisLayerWithTranslation: Layer = {
    ...egisLayer,
    name: t('landCover.egisMap2025')
  };
  
  // 상태 관리 - 그룹 레이어 구조
  const [layerGroups, setLayerGroups] = useState<LayerGroup[]>(() => initializeLayerGroups(t));
  const [backgroundMap, setBackgroundMap] = useState<BackgroundMapType>(() => {
    // localStorage에서 저장된 배경지도 설정 로드
    const savedBackgroundMap = localStorage.getItem('backgroundMap') as BackgroundMapType;
    return savedBackgroundMap && ['osm', 'cartodb', 'satellite', 'topo'].includes(savedBackgroundMap) 
      ? savedBackgroundMap 
      : 'satellite'; // 초기값을 위성지도로 변경
  });
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  
  // 모든 레이어를 평면화하여 반환하는 함수
  const getAllLayers = useCallback((): Layer[] => {
    return layerGroups.flatMap(group => group.layers);
  }, [layerGroups]);
  
  // 레이어 그룹 관리 함수들 (추가/삭제/토글 등)
  const updateLayerGroups = useCallback((updater: (groups: LayerGroup[]) => LayerGroup[]) => {
    setLayerGroups(updater);
  }, []);

  const addLayerToGroup = useCallback((groupId: string, layer: Layer) => {
    setLayerGroups(prevGroups =>
      prevGroups.map(group =>
        group.id === groupId
          ? { ...group, layers: [...group.layers, { ...layer, groupId }] }
          : group
      )
    );
  }, []);

  const removeLayerFromGroup = useCallback((groupId: string, layerId: string) => {
    setLayerGroups(prevGroups =>
      prevGroups.map(group =>
        group.id === groupId
          ? { ...group, layers: group.layers.filter(layer => layer.id !== layerId) }
          : group
      )
    );
  }, []);

  const toggleGroupExpanded = useCallback((groupId: string) => {
    setLayerGroups(prevGroups =>
      prevGroups.map(group =>
        group.id === groupId
          ? { ...group, expanded: !group.expanded }
          : group
      )
    );
  }, []);

  const toggleGroupVisible = useCallback((groupId: string) => {
    setLayerGroups(prevGroups =>
      prevGroups.map(group =>
        group.id === groupId
          ? { ...group, visible: !group.visible }
          : group
      )
    );
  }, []);

  const setGroupOpacity = useCallback((groupId: string, opacity: number) => {
    setLayerGroups(prevGroups =>
      prevGroups.map(group =>
        group.id === groupId
          ? { ...group, opacity }
          : group
      )
    );
  }, []);
  
  // 언어 변경 시 EGIS 레이어 이름 및 접속정보 동기화
  useEffect(() => {
    setLayerGroups(prevGroups => 
      prevGroups.map(group => 
        group.id === 'egis-group'
          ? {
              ...group,
              layers: group.layers.map(layer => 
                layer.type === LayerType.EGIS 
                  ? mergeEgisLayerSettings(layer, t('landCover.egisMap2025'))
                  : layer
              )
            }
          : group
      )
    );
  }, [t]);
  const [isLegendEditorOpen, setIsLegendEditorOpen] = useState(false);
  const [selectedLayerForLegend, setSelectedLayerForLegend] = useState<Layer | null>(null);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(() => {
    // 모바일에서는 기본적으로 접힌 상태로 시작
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return true;
    }
    return false;
  });
  const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(() => {
    // 모바일에서는 기본적으로 접힌 상태로 시작
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return true;
    }
    return false;
  });
  const mapRef = useRef<any>(null);
  
  // 화면 크기 변경 감지 및 모바일 모드에서 사이드바 자동 접기
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        // 모바일 모드로 변경 시 양쪽 사이드바를 모두 접기
        setIsLeftSidebarCollapsed(true);
        setIsRightSidebarCollapsed(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 모바일에서 지도 클릭/터치 시 사이드바 자동 접기
  const handleMapClick = useCallback(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      // 모바일에서 지도 클릭 시 양쪽 사이드바를 모두 접기
      setIsLeftSidebarCollapsed(true);
      setIsRightSidebarCollapsed(true);
    }
  }, []);
  
  // 지도 위치와 줌 상태 관리 - localStorage에서 초기값 로드
  const [mapCenter, setMapCenter] = useState<[number, number]>(() => {
    const savedMapCenter = localStorage.getItem('mapCenter');
    if (savedMapCenter) {
      try {
        return JSON.parse(savedMapCenter) as [number, number];
      } catch (error) {
        console.error('저장된 지도 위치 로드 실패:', error);
      }
    }
    return [36.5, 127.5]; // 기본값: 한국 중심 좌표 (서울 근처)
  });
  
  const [mapZoom, setMapZoom] = useState<number>(() => {
    const savedMapZoom = localStorage.getItem('mapZoom');
    if (savedMapZoom) {
      try {
        return JSON.parse(savedMapZoom) as number;
      } catch (error) {
        console.error('저장된 지도 줌 레벨 로드 실패:', error);
      }
    }
    return 7; // 기본값
  });
  
  // 오버레이 이미지와 bounds 상태 추가
  const [overlayImages, setOverlayImages] = useState<{ [key: string]: { url: string, bounds: [[number, number], [number, number]] } }>({});
  const [overlayBounds, setOverlayBounds] = useState<{ [key: string]: any }>({});
  
  // 사용자 정의 TIF 오버레이 이미지와 bounds를 비동기로 불러오는 함수 (최적화)
  const fetchOverlayInfo = useCallback(async (layerId: string, fileIndex: number) => {
    const overlayKey = `${layerId}-${fileIndex}`;
    
    // 이미 로드된 오버레이가 있으면 스킵
    if (overlayImages[overlayKey]) {
      return;
    }
    
    try {
      // 자동으로 범례 적용 여부 결정 (백엔드에서 밴드 수에 따라 결정)
      // 캐시 무효화를 위한 타임스탬프 추가
      const timestamp = Date.now();
      const url = `/api/upload/overlay/${layerId}/${fileIndex}?_t=${timestamp}`;
      
      // bounds 정보 (타임아웃 설정)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초 타임아웃
      
      let boundsJson: any = null;
      try {
        const boundsRes = await fetch(`/api/upload/overlay/${layerId}/${fileIndex}/bounds`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (!boundsRes.ok) {
          return;
        }
        boundsJson = await boundsRes.json();
        if (!boundsJson.bounds) {
          return;
        }
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name !== 'AbortError') {
          console.error('[오버레이] bounds 요청 에러:', layerId, fileIndex, error);
        }
        return;
      }
      
      // boundsJson이 정의되지 않았을 수 있으므로 다시 확인
      if (!boundsJson) {
        return;
      }
      
      setOverlayImages(prev => {
        // 이미 로드된 경우 스킵
        if (prev[overlayKey]) {
          return prev;
        }
        
        const next = {
          ...prev,
          [overlayKey]: {
            url,
            bounds: boundsJson.bounds
          }
        };
        return next;
      });
    } catch (e) {
      console.error(`[오버레이] fetchOverlayInfo 실패: ${layerId}-${fileIndex}`, e);
    }
  }, [getAllLayers]); // overlayImages 의존성 제거

  // 사용자 정의 TIF 파일이 보일 때마다 오버레이 정보 fetch (배치 처리로 최적화)
  useEffect(() => {
    const allLayers = getAllLayers();
    const visibleLayers = allLayers.filter(layer => layer.type === LayerType.USER_TIF && layer.visible);
    const overlayTasks: Array<{ layerId: string, fileIndex: number }> = [];
    
    visibleLayers.forEach(layer => {
      if (layer.files && layer.files.length > 0) {
        layer.files.forEach((file: any, fileIndex: number) => {
          if (file.visible !== false) {
            overlayTasks.push({ layerId: layer.id, fileIndex });
          }
        });
      }
    });
    
    // 배치 처리로 동시 요청 수 제한 (최대 5개씩)
    const batchSize = 5;
    const processBatch = async (tasks: Array<{ layerId: string, fileIndex: number }>) => {
      const batch = tasks.slice(0, batchSize);
      const remaining = tasks.slice(batchSize);
      
      try {
        await Promise.all(
          batch.map(({ layerId, fileIndex }) => fetchOverlayInfo(layerId, fileIndex))
        );
        
        // 남은 작업이 있으면 지연 후 다음 배치 처리
        if (remaining.length > 0) {
          setTimeout(() => processBatch(remaining), 100);
        }
      } catch (error) {
        console.error('오버레이 정보 fetch 중 오류:', error);
      }
    };
    
    if (overlayTasks.length > 0) {
      processBatch(overlayTasks);
    }
  }, [getAllLayers, fetchOverlayInfo]);

  // localStorage에서 사용자 정의 레이어 로드
  const loadLocalLayers = useCallback(async () => {
    try {
      console.log('=== 로컬 레이어 로드 시작 ===');
      
      // localStorage에서 저장된 그룹 상태 확인
      const savedGroups = localStorage.getItem('layerGroups');
      let savedUserLayers: Layer[] = [];
      
      if (savedGroups) {
        try {
          const parsedGroups = JSON.parse(savedGroups);
          const userGroup = parsedGroups.find((group: LayerGroup) => group.id === 'user-group');
          if (userGroup) {
            savedUserLayers = userGroup.layers || [];
          }
        } catch (error) {
          console.error('저장된 layerGroups 파싱 실패:', error);
        }
      }
      
      // 서버에서 최신 사용자 정의 레이어 로드
      const response = await fetch('/api/upload/layers/local');
      if (response.ok) {
        const localLayers = await response.json();
        
        // 로컬 레이어를 Layer 타입으로 변환하고 범례 데이터 로드
        const convertedLayers: Layer[] = await Promise.all(
          localLayers.map(async (localLayer: any) => {
            // 범례 데이터 로드
            let legendData = null;
            try {
              const legendResponse = await fetch(`/api/upload/layers/local/${localLayer.id}/legend?_t=${Date.now()}`);
              if (legendResponse.ok) {
                const legendResult = await legendResponse.json();
                if (legendResult.success && legendResult.data && legendResult.data.items && legendResult.data.items.length > 0) {
                  legendData = legendResult.data;
                  console.log(`범례 데이터 로드 성공: ${localLayer.id}`, legendData);
                } else {
                  console.log(`범례 데이터가 비어있음: ${localLayer.id}`);
                  legendData = null;
                }
              }
            } catch (error) {
              console.log(`범례 데이터 로드 실패: ${localLayer.id}`, error);
            }
            
            // 회전 설정 데이터 로드
            let rotationSettings = null;
            try {
              const rotationResponse = await fetch(`/api/upload/layers/local/${localLayer.id}/layer-settings`);
              if (rotationResponse.ok) {
                rotationSettings = await rotationResponse.json();
              }
            } catch (error) {
              console.log(`회전 설정 로드 실패: ${localLayer.id}`, error);
            }
            
            // 저장된 상태가 있으면 사용, 없으면 기본값
            const savedLayer = savedUserLayers.find(l => l.id === localLayer.id);
            const isVisible = savedLayer ? savedLayer.visible : false;
            const opacity = savedLayer ? savedLayer.opacity : (localLayer.opacity ?? 1.0);
            
            return {
              id: localLayer.id,
              name: localLayer.name,
              type: LayerType.USER_TIF,
              visible: isVisible,
              opacity: opacity,
              fileCount: localLayer.file_count,
              files: localLayer.files?.map((file: any, fileIndex: number) => ({
                ...file,
                visible: savedLayer?.files?.[fileIndex]?.visible ?? false
              })) || [],
              legendStyle: (savedLayer as any)?.legendStyle || legendData || null, // 저장된 범례 스타일 우선 사용
              rotationSettings: rotationSettings, // 회전 설정 추가
              metadata: legendData ? {
                legendStyle: legendData
              } : undefined,
              groupId: 'user-group'
            };
          })
        );
        
        // 사용자 그룹에 레이어 추가
        setLayerGroups(prevGroups => 
          prevGroups.map(group => 
            group.id === 'user-group'
              ? { ...group, layers: convertedLayers }
              : group
          )
        );
        
        console.log('로컬 레이어 로드 완료:', convertedLayers);
      }
    } catch (error) {
      console.error('로컬 레이어 로드 실패:', error);
    }
  }, []);

  // layerGroups를 localStorage에 저장하는 함수 (디바운싱 적용)
  const debouncedSaveLayerGroups = useMemo(
    () => debounce((groups: LayerGroup[]) => {
      localStorage.setItem('layerGroups', JSON.stringify(groups));
    }, 1000),
    []
  );

  // layerGroups가 변경될 때마다 localStorage에 저장
  useEffect(() => {
    debouncedSaveLayerGroups(layerGroups);
  }, [layerGroups, debouncedSaveLayerGroups]);

  // localStorage에서 layerGroups를 불러와서 상태 초기화 (EGIS 접속정보는 코드 기준 유지)
  useEffect(() => {
    const savedGroups = localStorage.getItem('layerGroups');
    if (savedGroups) {
      try {
        const parsedGroups = JSON.parse(savedGroups);
        setLayerGroups(mergeSavedLayerGroups(parsedGroups, t('landCover.egisMap2025')));
      } catch (error) {
        console.error('저장된 layerGroups 파싱 실패:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 컴포넌트 마운트 시 로컬 레이어 로드
  useEffect(() => {
    loadLocalLayers();
  }, [loadLocalLayers]);

  // 디바운싱된 localStorage 저장 함수들
  const debouncedSaveMapView = useMemo(
    () => debounce((center: [number, number], zoom: number) => {
      localStorage.setItem('mapCenter', JSON.stringify(center));
      localStorage.setItem('mapZoom', JSON.stringify(zoom));
    }, 1000),
    []
  );

  // 지도 위치와 줌 레벨을 localStorage에 저장
  const saveMapView = useCallback((center: [number, number], zoom: number) => {
    debouncedSaveMapView(center, zoom);
    setMapCenter(center);
    setMapZoom(zoom);
  }, [debouncedSaveMapView]);

  // 선택된 TIF 파일의 위치로 지도 중심 설정 함수
  const moveToSelectedTifFile = useCallback(async (layerId: string, fileIndex: number) => {
    try {
      const response = await fetch(`/api/upload/layers/local/${layerId}/bounds`);
      if (response.ok) {
        const boundsData = await response.json();
        if (boundsData.files && boundsData.files[fileIndex]) {
          const file = boundsData.files[fileIndex];
          const bounds = file.bounds;
          const centerLat = (bounds.north + bounds.south) / 2;
          const centerLng = (bounds.east + bounds.west) / 2;
          const map = mapRef.current;
          if (map) {
            const newCenter: [number, number] = [centerLat, centerLng];
            const newZoom = 18;
            map.setView(newCenter, newZoom);
            saveMapView(newCenter, newZoom);
            console.log(`선택된 TIF 파일로 지도 중심 이동: ${file.filename}`, centerLat, centerLng);
          }
        }
      }
    } catch (error) {
      console.error('선택된 TIF 파일 위치 가져오기 실패:', error);
    }
  }, [saveMapView]);

  // 지도 이동 이벤트 핸들러 (디바운싱 최적화)
  const handleMapMove = useCallback(() => {
    const map = mapRef.current;
    if (map) {
      const center = map.getCenter();
      const zoom = map.getZoom();
      saveMapView([center.lat, center.lng], zoom);
    }
  }, [saveMapView]);

  // 디바운스된 지도 이동 핸들러
  const debouncedMapMove = useCallback(() => {
    const timeoutId = setTimeout(handleMapMove, 100);
    return () => clearTimeout(timeoutId);
  }, [handleMapMove]);

  // EGIS 레이어 클릭 시 한국으로 지도 이동
  const moveToKorea = useCallback(() => {
    const map = mapRef.current;
    if (map) {
      // 한국 중심 좌표 (서울 근처)
      const koreaCenter: [number, number] = [36.5, 127.5];
      const koreaZoom = 7;
      map.setView(koreaCenter, koreaZoom);
      saveMapView(koreaCenter, koreaZoom);
      console.log('EGIS 토지피복지도 2025 클릭 - 한국으로 지도 이동');
    }
  }, [saveMapView]);

  // TIF 파일 선택 핸들러 (자동 이동 제거)
  const handleTifFileSelect = useCallback((layerId: string, fileIndex: number) => {
    setLayerGroups(prevGroups => 
      prevGroups.map(group => ({
        ...group,
        layers: group.layers.map(layer => 
          layer.id === layerId
            ? { ...layer, selectedFileIndex: fileIndex }
            : layer
        )
      }))
    );
    
    // 자동 이동 제거: 사용자가 더블클릭할 때만 이동
    // moveToSelectedTifFile(layerId, fileIndex);
  }, []);

  // TIF 파일 더블클릭 시 지도 이동 핸들러
  const handleTifFileDoubleClick = useCallback((layerId: string, fileIndex: number) => {
    console.log(`TIF 파일 더블클릭: ${layerId}, 파일 ${fileIndex}`);
    moveToSelectedTifFile(layerId, fileIndex);
  }, [moveToSelectedTifFile]);

  // 레이어 전체 영역으로 이동하는 핸들러
  const moveToLayerBounds = useCallback(async (layerId: string) => {
    try {
      console.log(`레이어 전체 영역으로 이동: ${layerId}`);
      
      // 새로운 total-bounds API 사용
      const response = await fetch(`/api/upload/layers/local/${layerId}/total-bounds`);
      if (response.ok) {
        const boundsData = await response.json();
        console.log('레이어 전체 bounds 데이터:', boundsData);
        
        if (boundsData && boundsData.bounds) {
          const bounds = boundsData.bounds;
          console.log('계산된 전체 bounds:', bounds);
          
          // 전체 영역의 중심점 계산
          const centerLat = (bounds.south + bounds.north) / 2;
          const centerLng = (bounds.west + bounds.east) / 2;
          
          // 영역 크기 계산 (로깅용)
          const latDiff = Math.abs(bounds.north - bounds.south);
          const lngDiff = Math.abs(bounds.east - bounds.west);
          const maxDiff = Math.max(latDiff, lngDiff);
          
          // Leaflet의 fitBounds를 사용하여 영역에 맞춰 자동으로 줌 조정
          const map = mapRef.current;
          if (map) {
            // Leaflet bounds 객체 생성 (southwest, northeast)
            const leafletBounds = [
              [bounds.south, bounds.west],  // southwest
              [bounds.north, bounds.east]   // northeast
            ];
            
            // 영역에 맞춰 자동으로 줌 조정 (패딩 추가로 여유 공간 확보)
            map.fitBounds(leafletBounds, {
              padding: [20, 20],  // 상하좌우 20px 여유 공간
              maxZoom: 18,        // 최대 줌 제한 (너무 확대되지 않도록)
              animate: true       // 부드러운 애니메이션
            });
            
            // 현재 줌 레벨 로깅
            const currentZoom = map.getZoom();
            console.log(`레이어 전체 영역으로 이동 완료: 영역 크기 ${maxDiff}°, 자동 줌: ${currentZoom}`);
            
            // 새로운 중심점과 줌을 저장
            const newCenter = map.getCenter();
            saveMapView([newCenter.lat, newCenter.lng], currentZoom);
          }
        } else {
          console.warn('전체 bounds 데이터가 없습니다. 첫 번째 파일 위치로 이동합니다.');
          // bounds가 없으면 첫 번째 파일 위치로 이동
          moveToSelectedTifFile(layerId, 0);
        }
      } else {
        console.warn('total-bounds API 호출 실패. 첫 번째 파일 위치로 이동합니다.');
        // API 호출 실패 시 첫 번째 파일 위치로 이동
        moveToSelectedTifFile(layerId, 0);
      }
    } catch (error) {
      console.error('레이어 전체 영역 이동 중 오류:', error);
      // 오류 발생 시 첫 번째 파일 위치로 이동
      moveToSelectedTifFile(layerId, 0);
    }
  }, [moveToSelectedTifFile, saveMapView]);



  // TIF 파일 경계 정보를 기반으로 지도 중심 설정 (사용자가 명시적으로 선택한 경우에만)
  useEffect(() => {
    const userTifLayers = getAllLayers().filter(layer => layer.type === LayerType.USER_TIF && layer.visible);
    if (userTifLayers.length > 0 && mapRef.current) {
      const layer = userTifLayers[0];
      
      // 사용자가 명시적으로 파일을 선택한 경우에만 지도 이동
      // 브라우저 새로고침 시에는 자동 이동하지 않음
      if (layer.selectedFileIndex !== undefined && layer.selectedFileIndex >= 0) {
        // 선택된 파일이 있고, 사용자가 명시적으로 선택한 경우에만 이동
        moveToSelectedTifFile(layer.id, layer.selectedFileIndex);
      }
    }
  }, [getAllLayers, moveToSelectedTifFile]);

  // 투명도 변경 핸들러
  const handleOpacityChange = useCallback((layerId: string, opacity: number) => {
    setLayerGroups(prevGroups => {
      const updatedGroups = prevGroups.map(group => {
        return {
          ...group,
          layers: group.layers.map(layer => 
            layer.id === layerId ? { ...layer, opacity } : layer
          )
        };
      });
      localStorage.setItem('layerGroups', JSON.stringify(updatedGroups));
      return updatedGroups;
    });
  }, []);

  // 레이어 변경 핸들러 - 그룹 구조에 맞게 수정
  const handleLayersChange = useCallback((newLayers: Layer[]) => {
    console.log('=== 레이어 상태 변경 시작 ===');
    console.log('변경된 레이어 순서:', newLayers.map(l => ({ id: l.id, name: l.name, type: l.type, visible: l.visible })));
    
    setLayerGroups(prevGroups => {
      const updatedGroups = prevGroups.map(group => {
        if (group.id === 'egis-group') {
          // EGIS 그룹: EGIS 레이어만 포함
          const egisLayers = newLayers.filter(layer => layer.type === LayerType.EGIS);
          return {
            ...group,
            layers: egisLayers.map(layer => ({
              ...layer,
              groupId: 'egis-group'
            }))
          };
        } else if (group.id === 'user-group') {
          // 사용자 그룹: 사용자 레이어만 포함
          const userLayers = newLayers.filter(layer => layer.type !== LayerType.EGIS);
          return {
            ...group,
            layers: userLayers.map(layer => ({
              ...layer,
              groupId: 'user-group'
            }))
          };
        }
        return group;
      });
      localStorage.setItem('layerGroups', JSON.stringify(updatedGroups));
      return updatedGroups;
    });
    console.log('localStorage에 레이어 순서 저장 완료');
    console.log('=== 레이어 상태 변경 완료 ===');
    
    // 사용자가 명시적으로 레이어를 선택한 경우에만 지도 이동
    // 자동 이동은 제거하여 브라우저 새로고침 시 지도 위치 유지
  }, [moveToSelectedTifFile]);

  // 배경지도 변경 핸들러
  const handleMapChange = useCallback((mapType: BackgroundMapType) => {
    setBackgroundMap(mapType);
    localStorage.setItem('backgroundMap', mapType);
    
    // 배경지도 변경 후 지도 리사이즈
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 100);
  }, []);

  // 범례 편집 핸들러
  const handleEditLegend = useCallback((layer: Layer) => {
    setSelectedLayerForLegend(layer);
    setIsLegendEditorOpen(true);
  }, []);

  // 범례 저장 핸들러
  const handleSaveLegendStyle = useCallback((layerId: string, legendStyle: any) => {
    setLayerGroups(prevGroups => {
      const updatedGroups = prevGroups.map(group => {
        if (group.id === 'user-group') {
          return {
            ...group,
            layers: group.layers.map(layer => {
              if (layer.id === layerId) {
                const updatedLayer = {
                  ...layer,
                  legendStyle: legendStyle, // metadata.legendStyle 대신 직접 legendStyle에 저장
                  opacity: legendStyle.opacity || layer.opacity
                };
                console.log('레이어 범례 스타일 업데이트:', layerId, '새 투명도:', updatedLayer.opacity);
                return updatedLayer;
              }
              return layer;
            })
          };
        }
        return group;
      });
      localStorage.setItem('layerGroups', JSON.stringify(updatedGroups));
      return updatedGroups;
    });
    
    // 범례 저장 후 오버레이 캐시 무효화 (투명도 등 렌더링 반영)
    setOverlayImages(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key.startsWith(`${layerId}-`)) {
          delete next[key];
        }
      });
      return next;
    });

    // 범례 편집 후 강제 새로고침으로 최신 데이터 반영
    setTimeout(() => {
      loadLocalLayers();
    }, 500);
    
    setIsLegendEditorOpen(false);
    setSelectedLayerForLegend(null);
  }, [loadLocalLayers]);

  // 파일 가시성 변경 핸들러
  const handleToggleFileVisibility = useCallback((layerId: string, fileIndex: number, visible: boolean) => {
    setLayerGroups(prevGroups => {
      const updatedGroups = prevGroups.map(group => {
        if (group.id === 'user-group') {
          return {
            ...group,
            layers: group.layers.map(layer => {
              if (layer.id === layerId && layer.files) {
                const updatedFiles = [...layer.files];
                if (updatedFiles[fileIndex]) {
                  updatedFiles[fileIndex] = { ...updatedFiles[fileIndex], visible };
                }
                
                // 파일 중 하나라도 체크되어 있으면 레이어를 활성화
                const hasVisibleFiles = updatedFiles.some(file => file.visible !== false);
                const updatedLayer = { 
                  ...layer, 
                  files: updatedFiles,
                  visible: hasVisibleFiles // 파일 가시성에 따라 레이어 가시성 결정
                };
                
                console.log(`파일 ${fileIndex} 가시성 변경: ${visible}, 레이어 활성화: ${hasVisibleFiles}`);
                return updatedLayer;
              }
              return layer;
            })
          };
        }
        return group;
      });
      localStorage.setItem('layerGroups', JSON.stringify(updatedGroups));
      return updatedGroups;
    });
  }, []);





  // 사용자 정의 레이어 새로고침 핸들러
  const handleRefreshLayers = useCallback(async () => {
    console.log('사용자 정의 레이어 새로고침 시작');
    
    // localStorage에서 사용자 설정 로드
    const savedGroups = localStorage.getItem('layerGroups');
    let userSettings: { [key: string]: { visible: boolean, opacity: number, files?: any[] } } = {};
    
    if (savedGroups) {
      try {
        const parsedGroups = JSON.parse(savedGroups);
        parsedGroups.forEach((group: LayerGroup) => {
          if (group.id === 'user-group') {
            group.layers.forEach((layer: Layer) => {
              if (layer.type !== LayerType.EGIS) {
                userSettings[layer.id] = {
                  visible: layer.visible,
                  opacity: layer.opacity,
                  files: layer.files
                };
              }
            });
          }
        });
      } catch (error) {
        console.error('localStorage 파싱 실패:', error);
      }
    }
    
    // 서버에서 최신 사용자 정의 레이어 로드
    try {
      const response = await fetch('/api/upload/layers/local');
      if (response.ok) {
        const localLayers = await response.json();
        
        // 로컬 레이어를 Layer 타입으로 변환하고 범례 데이터 로드
        const convertedLayers: Layer[] = await Promise.all(
          localLayers.map(async (localLayer: any) => {
            // 범례 데이터 로드
            let legendData = null;
            try {
              const legendResponse = await fetch(`/api/upload/layers/local/${localLayer.id}/legend?_t=${Date.now()}`);
              if (legendResponse.ok) {
                const legendResult = await legendResponse.json();
                if (legendResult.success && legendResult.data && legendResult.data.items && legendResult.data.items.length > 0) {
                  legendData = legendResult.data;
                  console.log(`범례 데이터 로드 성공: ${localLayer.id}`, legendData);
                } else {
                  console.log(`범례 데이터가 비어있음: ${localLayer.id}`);
                  legendData = null;
                }
              }
            } catch (error) {
              console.log(`범례 데이터 로드 실패: ${localLayer.id}`, error);
            }
            
            // 회전 설정 데이터 로드
            let rotationSettings = null;
            try {
              const rotationResponse = await fetch(`/api/upload/layers/local/${localLayer.id}/layer-settings`);
              if (rotationResponse.ok) {
                rotationSettings = await rotationResponse.json();
              }
            } catch (error) {
              console.log(`회전 설정 로드 실패: ${localLayer.id}`, error);
            }
            
            // 사용자 설정 적용 (저장된 설정이 있으면 사용, 없으면 기본값)
            const userSetting = userSettings[localLayer.id];
            const isVisible = userSetting ? userSetting.visible : false;
            const opacity = userSetting ? userSetting.opacity : (localLayer.opacity ?? 1.0);
            
            return {
              id: localLayer.id,
              name: localLayer.name,
              type: LayerType.USER_TIF,
              visible: isVisible,
              opacity: opacity,
              fileCount: localLayer.file_count,
              files: localLayer.files?.map((file: any, fileIndex: number) => ({
                ...file,
                visible: userSetting?.files?.[fileIndex]?.visible ?? false
              })) || [],
              legendStyle: (userSetting as any)?.legendStyle || legendData || null, // 저장된 범례 스타일 우선 사용
              rotationSettings: rotationSettings, // 회전 설정 추가
              metadata: legendData ? {
                legendStyle: legendData
              } : undefined,
              groupId: 'user-group'
            };
          })
        );
        
        // 사용자 그룹에 레이어 추가 (EGIS 레이어는 제외)
        setLayerGroups(prevGroups => 
          prevGroups.map(group => 
            group.id === 'user-group'
              ? { ...group, layers: convertedLayers }
              : group
          )
        );
        
        // 오버레이 이미지 캐시 무효화 (회전 설정이 변경되었을 수 있으므로)
        setOverlayImages({});
        
        console.log('사용자 정의 레이어 새로고침 완료:', convertedLayers.map((l: Layer) => ({ id: l.id, name: l.name, type: l.type, visible: l.visible })));
      } else {
        console.error('서버에서 레이어 로드 실패');
      }
    } catch (error) {
      console.error('사용자 정의 레이어 새로고침 실패:', error);
    }
  }, []);



  // 사이드바 토글 핸들러 (지도 리사이즈 포함)
  const handleLeftSidebarToggle = useCallback(() => {
    const newLeftCollapsed = !isLeftSidebarCollapsed;
    setIsLeftSidebarCollapsed(newLeftCollapsed);
    
    // 모바일에서 왼쪽 사이드바가 펼쳐지면 오른쪽 사이드바를 접기
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && !newLeftCollapsed) {
      setIsRightSidebarCollapsed(true);
    }
    
    // 지도 리사이즈를 위한 지연 (더 안정적인 타이밍)
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 400);
  }, [isLeftSidebarCollapsed]);

  const handleRightSidebarToggle = useCallback(() => {
    const newRightCollapsed = !isRightSidebarCollapsed;
    setIsRightSidebarCollapsed(newRightCollapsed);
    
    // 모바일에서 오른쪽 사이드바가 펼쳐지면 왼쪽 사이드바를 접기
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && !newRightCollapsed) {
      setIsLeftSidebarCollapsed(true);
    }
    
    // 지도 리사이즈를 위한 지연 (더 안정적인 타이밍)
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 400);
  }, [isRightSidebarCollapsed]);

  // EGIS 레이어 에러 처리 상태
  const [egisLayerError, setEgisLayerError] = useState<boolean>(false);
  
  // EGIS 레이어 에러 핸들러
  const handleEgisLayerError = useCallback(() => {
    console.warn('EGIS 레이어 로드 실패 - 폴백 모드로 전환');
    setEgisLayerError(true);
  }, []);
  
  // EGIS 레이어 성공 핸들러
  const handleEgisLayerLoad = useCallback(() => {
    console.log('EGIS 레이어 로드 성공');
    setEgisLayerError(false);
  }, []);

  return (
    <div className="app">
      {/* 헤더 */}
      <div className="header">
        <div className="header-content">
          <h1 className="logo">EcoVision</h1>
          <div className="header-controls">
            <LanguageSelector />
            <button 
              className="header-btn"
              onClick={() => setIsHelpModalOpen(true)}
              title={t('help.title') as string}
            >
              ❓ {t('help.button')}
            </button>
          </div>
        </div>
      </div>

      <div className="main-container">
        <div className={`sidebar left-sidebar ${isLeftSidebarCollapsed ? 'collapsed' : ''}`}>
          <QGISLayerPanel
            layerGroups={layerGroups}
            onLayersChange={handleLayersChange}
            onToggleFileVisibility={handleToggleFileVisibility}
            onTifFileSelect={handleTifFileSelect}
            onTifFileDoubleClick={handleTifFileDoubleClick}
            onLayerDoubleClick={moveToLayerBounds}
            onEgisLayerClick={moveToKorea}
            onRefreshLayers={handleRefreshLayers}
            isCollapsed={isLeftSidebarCollapsed}
            onToggleCollapsed={handleLeftSidebarToggle}
          />
        </div>

        <div className="map-container">
          {/* 왼쪽 사이드바 토글 버튼 */}
          {isLeftSidebarCollapsed && (
                      <button 
            className="sidebar-toggle-overlay left-toggle"
            onClick={handleLeftSidebarToggle}
            title={t('common.toggleLeftSidebar') as string}
          >
            ▶
          </button>
          )}
          
          {/* 오른쪽 사이드바 토글 버튼 */}
          {isRightSidebarCollapsed && (
                      <button 
            className="sidebar-toggle-overlay right-toggle"
            onClick={handleRightSidebarToggle}
            title={t('common.toggleRightSidebar') as string}
          >
            ◀
          </button>
          )}
          
          <MapContainer
            ref={mapRef}
            center={mapCenter}
            zoom={mapZoom}
            minZoom={0} // 최소 줌 레벨 추가
            maxZoom={22}
            style={{ height: '100%', width: '100%' }}
          >
            <MapResizer onMapMove={handleMapMove} />
            <MapClickHandler onMapClick={handleMapClick} />
            <TileLayer
              url={backgroundMaps[backgroundMap].url}
              attribution={backgroundMaps[backgroundMap].attribution}
              minZoom={backgroundMaps[backgroundMap].minZoom} // 최소 줌 레벨 추가
              maxZoom={22} // 사용자가 확대할 수 있는 최대 줌 (이전 배율 이미지 확대)
              maxNativeZoom={backgroundMaps[backgroundMap].maxNativeZoom} // 실제 타일 서버 최대 줌
              tileSize={backgroundMaps[backgroundMap].tileSize}
              keepBuffer={3} // 버퍼 크기 증가
              updateWhenZooming={true} // 줌 중에도 업데이트
              updateWhenIdle={true}
              zoomOffset={0}
              key={`tile-${backgroundMap}`} // 배경지도 변경 시 타일 레이어 재생성
            />
            
            {/* EGIS WMS 레이어 - 에러 처리 강화 */}
            {getAllLayers().filter(layer => layer.type === LayerType.EGIS && layer.visible).map(layer => (
              <WMSTileLayer
                key={layer.id}
                url={layer.url || ''}
                params={{
                  layers: layer.params?.layers || 'EGIS:lv3_2025y',
                  format: layer.params?.format || 'image/png',
                  transparent: layer.params?.transparent || true,
                  version: layer.params?.version || '1.1.1',
                  service: layer.params?.service || 'WMS',
                  request: layer.params?.request || 'GetMap'
                }}
                opacity={layer.opacity}
                attribution="© EGIS"
                maxZoom={22}
                // 안정적인 설정
                tileSize={256}
                keepBuffer={2}
                updateWhenZooming={false}
                updateWhenIdle={true}
                // 에러 처리 강화
                errorTileUrl="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
              />
            ))}
            
            {/* 사용자 정의 TIF 파일 오버레이 렌더링 */}
            {getAllLayers().filter(layer => layer.type === LayerType.USER_TIF && layer.visible).map(layer => {
              if (layer.files && layer.files.length > 0) {
                return layer.files.map((file: any, fileIndex: number) => {
                  if (file.visible !== false) {
                    const overlayKey = `${layer.id}-${fileIndex}`;
                    const overlay = overlayImages[overlayKey];
                    if (overlay && overlay.bounds) {
                      
                      return (
                        <ImageOverlay
                          key={overlayKey}
                          url={overlay.url}
                          bounds={overlay.bounds}
                          opacity={layer.opacity}
                        />
                      );
                    } else {
                      // 오버레이 데이터가 없음 (정상적인 상황)
                    }
                  }
                  return null;
                });
              }
              return null;
            })}
            {/* 기존 WMSTileLayer 기반 사용자 정의 TIF 렌더링은 주석 처리 또는 제거 */}
          </MapContainer>
        </div>
        
        <div className={`sidebar right-sidebar ${isRightSidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <h3>{t('common.settingsAndLegend')}</h3>
            <button 
              className="collapse-btn"
              onClick={handleRightSidebarToggle}
              title={t('common.toggleRightSidebar') as string}
            >
              {isRightSidebarCollapsed ? "◀" : "▶"}
            </button>
          </div>
          
          <div className="sidebar-content">
            <div className="background-map-selector">
              <h3>{t('map.backgroundMap')}</h3>
              <select
                value={backgroundMap}
                onChange={(e) => handleMapChange(e.target.value as BackgroundMapType)}
              >
                {Object.entries(backgroundMaps).map(([key, map]) => (
                  <option key={key} value={key}>
                    {map.name}
                  </option>
                ))}
              </select>
            </div>
            

            
            <LegendPanel 
              layerGroups={layerGroups}
              onEditLegend={handleEditLegend}
              onOpacityChange={handleOpacityChange}
            />
          </div>
        </div>
      </div>

      {isLegendEditorOpen && selectedLayerForLegend && (
        <LegendEditor
          layer={selectedLayerForLegend}
          onSave={(legendStyle) => handleSaveLegendStyle(selectedLayerForLegend.id, legendStyle)}
          onClose={() => {
            setIsLegendEditorOpen(false);
            setSelectedLayerForLegend(null);
          }}
        />
      )}

      {/* 도움말 모달 */}
      <HelpModal 
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  );
}

export default App;
